import {
    useQuery,
} from "@tanstack/react-query";
import {
    ChevronLeft,
    LayoutGrid,
    List,
} from "lucide-preact";
import {
    useCallback,
    useState,
} from "preact/compat";
import {
    Link,
    useSearchParams,
} from "react-router-dom";

import {
    splClient,
} from "../api/client.ts";
import {
    AiAnalysisTab,
} from "../components/admin/AiAnalysisTab.tsx";
import {
    HealthDashboard,
} from "../components/admin/HealthDashboard.tsx";
import {
    LibraryTab,
} from "../components/admin/LibraryTab.tsx";
import {
    ManageTabBar,
} from "../components/admin/ManageTabBar.tsx";
import {
    ProcessingQueueTab,
} from "../components/admin/ProcessingQueueTab.tsx";
import {
    ReviewQueueTab,
} from "../components/admin/ReviewQueueTab.tsx";
import {
    TagsTab,
} from "../components/admin/TagsTab.tsx";
import {
    KeyboardShortcutsModal,
} from "../components/common/KeyboardShortcutsModal.tsx";
import {
    useManageKeyboardShortcuts,
} from "../hooks/index.ts";
import {
    useManageStore,
} from "../stores/manageStore.ts";

type TabId =
    | "health"
    | "processing"
    | "review"
    | "library"
    | "tags"
    | "ai-analysis";

export default function ManagePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = (searchParams.get("tab") as TabId) || "library";
    const [showShortcutsModal, setShowShortcutsModal] = useState(false);

    const {
        viewMode,
        thumbnailSize,
        setViewMode,
        setThumbnailSize,
    } = useManageStore();

    const setActiveTab = useCallback((tab: TabId): void => {
        const params = new URLSearchParams();
        params.set("tab", tab);
        setSearchParams(params, {
            replace: true,
        });
    }, [setSearchParams]);

    useManageKeyboardShortcuts({
        onTabSwitch: (tabId) => setActiveTab(tabId as TabId),
        onShowHelp: () => setShowShortcutsModal(true),
        onEscape: () => setShowShortcutsModal(false),
    });

    // lightweight polling for tab badge counts
    const {
        data: queueStatsData,
    } = useQuery({
        queryKey: ["queueStatsBadge"],
        queryFn: () => splClient.getJobStats(),
        refetchInterval: 30000,
    });

    const {
        data: reviewData,
    } = useQuery({
        queryKey: ["reviewItemsBadge"],
        queryFn: () => splClient.getReviewItems(1),
        refetchInterval: 30000,
    });

    const {
        data: aiStatsData,
    } = useQuery({
        queryKey: ["aiStatsBadge"],
        queryFn: () => splClient.getAiStats(),
        refetchInterval: 30000,
    });

    const processingCount = queueStatsData
        ? queueStatsData.pending + queueStatsData.failed +
            queueStatsData.cancelled
        : undefined;
    const reviewCount = reviewData?.total_count;

    return (
        <div className="min-h-screen bg-bg-primary text-text-primary pb-20">
            {/* Header */}
            <header className="sticky top-0 bg-bg-secondary border-b border-border z-30 p-4">
                <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1"
                        >
                            <ChevronLeft size={20} />
                            Back to Viewer
                        </Link>
                        <h1 className="text-xl font-semibold">Manage</h1>
                    </div>

                    {/* View mode toggle - only relevant for library tab */}
                    {activeTab === "library" && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setViewMode("grid")}
                                className={`p-2 rounded transition-colors ${
                                    viewMode === "grid"
                                        ? "bg-accent text-white"
                                        : "bg-bg-tertiary text-text-secondary hover:text-text-primary"
                                }`}
                            >
                                <LayoutGrid size={20} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("list")}
                                className={`p-2 rounded transition-colors ${
                                    viewMode === "list"
                                        ? "bg-accent text-white"
                                        : "bg-bg-tertiary text-text-secondary hover:text-text-primary"
                                }`}
                            >
                                <List size={20} />
                            </button>

                            {viewMode === "grid" && (
                                <div className="flex items-center gap-1 ml-2 border-l border-border pl-2">
                                    {(["small", "medium", "large"] as const)
                                        .map((size) => (
                                            <button
                                                type="button"
                                                key={size}
                                                onClick={() =>
                                                    setThumbnailSize(size)}
                                                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                                    thumbnailSize === size
                                                        ? "bg-accent text-white"
                                                        : "bg-bg-tertiary text-text-secondary hover:text-text-primary"
                                                }`}
                                                title={`${
                                                    size.charAt(0)
                                                        .toUpperCase() +
                                                    size.slice(1)
                                                } thumbnails`}
                                            >
                                                {size.charAt(0).toUpperCase()}
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <ManageTabBar
                activeTab={activeTab}
                onTabChange={setActiveTab}
                processingCount={processingCount}
                reviewCount={reviewCount}
                aiAnalysisCount={aiStatsData?.analyzed_count}
            />

            {activeTab === "health" && <HealthDashboard />}
            {activeTab === "processing" && <ProcessingQueueTab />}
            {activeTab === "review" && <ReviewQueueTab />}
            {activeTab === "library" && <LibraryTab />}
            {activeTab === "tags" && <TagsTab />}
            {activeTab === "ai-analysis" && <AiAnalysisTab />}

            <KeyboardShortcutsModal
                isOpen={showShortcutsModal}
                onClose={() => setShowShortcutsModal(false)}
                context="admin"
            />
        </div>
    );
}
